// Custom Flatpickr Stimulus controller that extends stimulus-flatpickr
// to add a clear button for touch devices (iPad) where the keyboard
// does not appear to allow backspace/delete of date values.
import Flatpickr from "stimulus-flatpickr"

export default class extends Flatpickr {
  connect() {
    super.connect()
    if (!this._clearButton) {
      this._insertClearButton()
    }
  }

  disconnect() {
    this._removeClearButton()
    super.disconnect()
  }

  // stimulus-flatpickr hook called when the date changes
  change(selectedDates, dateStr, instance) {
    this._updateClearButtonVisibility()
  }

  _insertClearButton() {
    this._boundClearDate = this._clearDate.bind(this)
    this._boundUpdateVisibility = this._updateClearButtonVisibility.bind(this)

    var btn = document.createElement("button")
    btn.type = "button"
    btn.className = "flatpickr-clear-btn"
    btn.textContent = "Clear"
    btn.setAttribute("aria-label", "Clear date")
    btn.addEventListener("click", this._boundClearDate)

    // Find a positioned wrapper so the absolutely-positioned clear button
    // anchors correctly. Prefer a semantic .input-wrapper ancestor; otherwise
    // fall back to the input's immediate parent. We must NOT relocate the
    // input itself: it carries data-controller="flatpickr", so moving it in
    // the DOM makes Stimulus disconnect and reconnect the controller, which
    // re-runs connect() and re-wraps the input endlessly — an infinite loop
    // that freezes the page with no console error. Appending only the new
    // button leaves the controller element in place, so no reconnect fires.
    var wrapper = this.element.closest(".input-wrapper") || this.element.parentNode
    wrapper.appendChild(btn)
    this._clearButton = btn

    // Set up positioned wrapper after button is in the DOM so we can
    // measure the button's rendered width for the margin reservation.
    this._ensurePositionedWrapper(wrapper, btn)

    // Listen for input/change events on the visible input to keep the
    // clear button visibility in sync when dates are changed programmatically
    // or via keyboard.
    var visibleInput = (this.fp && this.fp.altInput) ? this.fp.altInput : this.element
    this._visibleInput = visibleInput
    visibleInput.addEventListener("input", this._boundUpdateVisibility)
    visibleInput.addEventListener("change", this._boundUpdateVisibility)

    this._updateClearButtonVisibility()
  }

  _removeClearButton() {
    if (this._clearButton) {
      this._clearButton.removeEventListener("click", this._boundClearDate)
      this._clearButton.remove()
      this._clearButton = null
    }
    if (this._visibleInput) {
      this._visibleInput.removeEventListener("input", this._boundUpdateVisibility)
      this._visibleInput.removeEventListener("change", this._boundUpdateVisibility)
      this._visibleInput = null
    }
    if (this._wrapper) {
      this._wrapper.style.marginRight = this._originalMarginRight || ""
      this._wrapper.style.position = this._originalPosition || ""
      this._wrapper = null
    }
  }

  _clearDate(event) {
    event.preventDefault()
    event.stopPropagation()
    this.fp.clear()
    this._updateClearButtonVisibility()
  }

  _updateClearButtonVisibility() {
    if (this._clearButton) {
      // Use flatpickr's selectedDates as the source of truth for whether a
      // date is set. this.element.value can be out of sync with the visible
      // input when altInput mode is enabled.
      var hasValue = this.fp && this.fp.selectedDates
        ? this.fp.selectedDates.length > 0
        : !!this.element.value
      this._clearButton.style.display = hasValue ? "" : "none"
    }
  }

  _ensurePositionedWrapper(wrapper, btn) {
    // Capture original inline styles so they can be restored on disconnect.
    this._originalMarginRight = wrapper.style.marginRight
    this._originalPosition = wrapper.style.position

    // If the wrapper has static positioning, set it to relative so the
    // absolutely-positioned clear button anchors to it correctly.
    var position = window.getComputedStyle(wrapper).position
    if (position === "static") {
      wrapper.style.position = "relative"
    }
    // Reserve horizontal space for the absolutely-positioned button by
    // adding its rendered width to the wrapper's existing right margin.
    // getBoundingClientRect().width gives the true rendered width in px
    // regardless of CSS unit (px, rem, etc.), with a 44px fallback for
    // environments where layout isn't computed (e.g. JSDOM in tests).
    var existingMargin = parseFloat(getComputedStyle(wrapper).marginRight) || 0
    var btnWidth = btn.getBoundingClientRect().width || btn.offsetWidth || 44
    wrapper.style.marginRight = (existingMargin + btnWidth) + "px"
    this._wrapper = wrapper
  }
}
